/// <reference types="vite/client" />
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Clarity from "@microsoft/clarity";
import { router } from "./router";
import { Toaster } from "@/components/ui/sonner";
import "./styles/globals.css";
import "./styles/tk.css";
// Boot-time guard: verifies TEMPLATES rows agree with the variant/layout
// dispatchers. DEV throws on mismatch, PROD logs. See lib/catalogContract.ts.
import "@/lib/catalogContract";

// ── Microsoft Clarity analytics ────────────────────────────────────────
// Free behavioural analytics (session recordings, heatmaps, click/scroll)
// so the operator can see how visitors use the catalog + editor. Loads the
// clarity.ms tag at startup. Prod-gated via import.meta.env.PROD so local
// dev sessions don't pollute the analytics with developer noise.
const CLARITY_PROJECT_ID = "wv459ralnc";
if (import.meta.env.PROD) {
  Clarity.init(CLARITY_PROJECT_ID);
}

// ── Mobile motion shim ─────────────────────────────────────────────────
// Templates gate their bespoke animations behind `prefers-reduced-motion`
// via window.matchMedia. On phones (≤768px) we want the same "static page"
// behaviour without requiring the user's OS setting. This wrapper makes
// matchMedia('(prefers-reduced-motion: reduce)') always report matches:true
// when the viewport is phone-sized — so every variant's IntersectionObserver
// reveal, drift confetti, paint-clip, parallax, live-typing, etc. all
// short-circuit to their reduced-motion fallback.
//
// Runs once before React mounts. Listens to viewport resize so flipping
// between mobile/desktop in dev tools updates correctly.
(() => {
  if (typeof window === "undefined" || !window.matchMedia) return;
  const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
  const MOBILE = "(max-width: 768px)";
  const original = window.matchMedia.bind(window);

  function shim(query: string): MediaQueryList {
    const realResult = original(query);
    if (query !== REDUCED_MOTION) return realResult;
    const mobileQ = original(MOBILE);
    // Build a MediaQueryList-like object that reports true if EITHER the
    // OS prefers-reduced-motion OR the viewport is phone-sized.
    const listeners = new Set<(e: MediaQueryListEvent) => void>();
    const compute = () => realResult.matches || mobileQ.matches;
    const obj: MediaQueryList = {
      get matches() { return compute(); },
      media: query,
      onchange: null,
      addEventListener: ((type: string, cb: EventListener) => {
        if (type === "change") listeners.add(cb as (e: MediaQueryListEvent) => void);
      }) as MediaQueryList["addEventListener"],
      removeEventListener: ((type: string, cb: EventListener) => {
        if (type === "change") listeners.delete(cb as (e: MediaQueryListEvent) => void);
      }) as MediaQueryList["removeEventListener"],
      addListener: ((cb: (e: MediaQueryListEvent) => void) => listeners.add(cb)) as MediaQueryList["addListener"],
      removeListener: ((cb: (e: MediaQueryListEvent) => void) => listeners.delete(cb)) as MediaQueryList["removeListener"],
      dispatchEvent: (e: Event) => realResult.dispatchEvent(e),
    } as MediaQueryList;
    const fanOut = () => {
      const ev = { matches: compute(), media: query } as MediaQueryListEvent;
      listeners.forEach((cb) => cb(ev));
    };
    realResult.addEventListener?.("change", fanOut);
    mobileQ.addEventListener?.("change", fanOut);
    return obj;
  }
  window.matchMedia = shim;
})();

// A chunk or its CSS failing to preload (stale tab after a deploy, a flaky
// edge) reloads the page once instead of surfacing a router error.
window.addEventListener("vite:preloadError", (event) => {
  const last = Number(sessionStorage.getItem("chunkReloadedAt") || 0);
  if (Date.now() - last < 30_000) return;
  event.preventDefault();
  sessionStorage.setItem("chunkReloadedAt", String(Date.now()));
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>
);
